import { useCallback, useMemo } from 'react';
import { isTransparentColor } from '@elfsight/lib-utils';
import { useSettings } from '@wu/widget-core';
import type { FeedsSettings } from '../../types';
import type { SourceValue } from '../data/sources';
import { sourceValidation } from '../data/utils';
import type { PreparedPreviewsSourcesType } from './getPreviewSources';
import { getPreviewSources } from './getPreviewSources';

type Modifier<T> = {
  [P in keyof Partial<T>]: (value: T[P], allSettings: T) => T[P];
};

type PreviewSourceModifierType = <
  T extends keyof PreparedPreviewsSourcesType,
  P
>(
  key: T,
  value: P
) => PreparedPreviewsSourcesType[T] | P;

const isPristine = (sources: SourceValue[]) =>
  !sources.some((source) => sourceValidation(source));

export function useSocialFeedSettings(): FeedsSettings {
  const settings: FeedsSettings = useSettings();

  const withPreviewSources = useMemo(
    () => isPristine(settings.sources),
    [settings.sources]
  );

  const previewSources = useMemo(
    () => getPreviewSources(settings.previewSources, settings.previewSort),
    [settings.previewSources, settings.previewSort]
  );

  const previewSourcesModifier = useCallback<PreviewSourceModifierType>(
    (key, value) => (withPreviewSources ? previewSources[key] : value),
    [withPreviewSources, previewSources]
  );

  const modifiers = useMemo(
    () =>
      ({
        sources: (value) =>
          previewSourcesModifier(
            'sources',
            value.filter((item) => sourceValidation(item))
          ),
        sort: (value) => previewSourcesModifier('sort', value),
        postHeightNormalization: (
          value,
          { layout, carouselRows, postStyle, postBackgroundColor }
        ) => {
          const isTransparentBackgroundColor =
            postStyle === 'custom' && isTransparentColor(postBackgroundColor);
          const isSuitableLayout =
            layout === 'grid' || (layout === 'carousel' && carouselRows === 1);

          return value && !isTransparentBackgroundColor && isSuitableLayout;
        }
        // @ts-expect-error Есть проблемы с правильной типизацией, но все работает корректно
      }) satisfies Modifier<FeedsSettings>,
    [previewSourcesModifier]
  );

  return useMemo(() => {
    const modifiedSettings: FeedsSettings = { ...settings };
    const settingsKeys = Object.keys(modifiers);

    settingsKeys.forEach((key) => {
      if (modifiers[key]) {
        // @ts-expect-error Есть проблемы с правильной типизацией, но все работает корректно
        modifiedSettings[key] = modifiers[key](settings[key], settings);
      }
    });

    return modifiedSettings;
  }, [settings, modifiers]);
}
